import { haversineDistanceMeters, validateCustomIntersectPoint } from "../../utils/locationUtils";
import { getUSandDSDists } from "../../utils/poi/poiHookHelpers";
import { useError } from "../../contexts/ErrorContext";
import { useLoading } from "../../contexts/LoadingContext"
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { useSuccess } from "../../contexts/SuccessContext";
import { getLinkedPOIsAPI } from "../../services/poiGroupsAPI"
import { getPOIEstimatedPassTimeAPI } from "../../services/poiAPI"
import { findClosestSegment } from "../../utils/locationUtils";
/**
 * Exports functions that can be used with PoiDialog and PoiGroupDialog boxes, hooks into state and functionality from other poi hooks
 * 
 * @param {Object} state - state from usePoiMap
 * @param {Object} setters - setters from usePoiMap
 * @param {Object} hooks - other poi hooks
 * 
 * @returns {Function} handlePoiSave
 * @returns {Function} handleDialogCancel
 * @returns {Function} handlePoiNameChange
 * @returns {Function} snapTo
 * @returns {Function} handleUpdatePoiGroups
 * @returns {Function} handleAddNewPoiGroup
 * @returns {Function} handleChangeActiveComponent - changes active component on button press in dialog box
 * @returns {Function} handleUpdateLinkedPOI - when updating from PoiGroupDialog or when a group is selected from dropdown, update LinkedPOI name and description, not original
 * 
 * @category Hooks - POI Map
 * 
 * @todo logic getting complex throughout POI code, especially after adding more permissions - maybe we can refactor
 */
const usePoiDialogHandlers = ({
    state: { iwData, currentLine, currentClickedPoint, selectedPoiGroup, selectedPoiRun, polyLineArr },
    setters: { setIwData, setCurrentLine, setCurrentClickedPoint, setActiveComponent, setSelectedPoiGroup},
    hooks: { poiService, poiGroupService, polylineManager, markerManager, infoWindow }
}) => {
    const { showError } = useError();
    const { showLoading, hideLoading } = useLoading();
    const {hasUnsavedChanges, setHasUnsavedChanges, requestConfirm} = useUnsavedChanges()
    const {timeoutSuccess} = useSuccess()

    /*********************************************************************** */
    /* GENERAL DIALOG HANDLERS
    /*********************************************************************** */

    const handlePoiSave = async (poiJob, poiGroup = null, closeWindow = true) => {
        showLoading("Saving...");
        
        const poiID = currentLine?.start?.POIID ?? null;

        //if saving from group view, want POI_Name Description to be saved on linkedPOI, not original
        //if existing POI, and a group is selected, don't send POI_Name and Description
        //if new POI, or submitting from All POIs view, send them
        const updatingFromGroupView = !selectedPoiGroup?.label.includes("All POIs") && poiID

        const poiData = {
            ...(!updatingFromGroupView && { POI_Name: iwData.POI_Name }),
            ...(!updatingFromGroupView && { Description: iwData.Description }),
            Intersect_Point_Lat: iwData.lineIntersect.lat,
            Intersect_Point_Lng: iwData.lineIntersect.lng,
            Click_Point_Lat: iwData.lat,
            Click_Point_Lng: iwData.lng,
            JobID: Number(poiJob),
            US_Site: iwData.upstreamAndDownstreamAgms.upstreamAgm.AGMID,
            DS_Site: iwData.upstreamAndDownstreamAgms.downstreamAgm.AGMID,
            Dist_To_US: iwData.usDsDistances.distToUS,
            Dist_To_DS: iwData.usDsDistances.distToDS,
            POIID: poiID,
            CreatedBy: iwData.CreatedBy?.UserID ?? null
        };

        const response = await poiService.savePOI(poiData, !poiID);

        if (response.success) {

            const updatedPoiID = response.data.POIID ?? poiID;
            const createdBy = iwData.CreatedBy ?? response.data.CreatedBy //if this is a newly created POI, get CreatedBy from response so it can be displayed on info window

            let groupResponse
            let linkedPOIsResponse


            if(poiGroup){
                groupResponse = await poiGroupService.addPoiToGroup(
                    poiGroup.POIGroupID, 
                    {
                        POI_Name: iwData.POI_Name,
                        Description: iwData.Description,
                        POIID: updatedPoiID
                    },
                    poiJob
                )
    
                if(!groupResponse.success){
                    showError(`There was a problem adding this POI to the selected group: ${groupResponse.message}`);
                } else {
                    linkedPOIsResponse = await getLinkedPOIsAPI(updatedPoiID)

                }
            }
            
            //if we added poi to a group (new poi), add it to clickedPoints data - otherwise, just use original data
            const iwPoiGroups = groupResponse?.success ? groupResponse.data.updatedGroups : iwData.poiGroups
            const updatedLinkedPOIs = linkedPOIsResponse?.success ? linkedPOIsResponse.data : iwData.linkedPOIs
            const currentLineID = currentLine?.start?.lineID;

            poiService.setClickedPoints((prevPoints) =>
                prevPoints.map((point) =>
                        point.lineID != null && point.lineID === currentLineID
                        ? { 
                            ...point, 
                            ...(!updatingFromGroupView && { POI_Name: iwData.POI_Name }),
                            ...(!updatingFromGroupView && { Description: iwData.Description }),
                            POIID: updatedPoiID, 
                            CreatedBy: createdBy, 
                            poiGroups: iwPoiGroups,
                            linkedPOIs: updatedLinkedPOIs
                        }
                        : point
                )
            );
            
            setHasUnsavedChanges(false)

            //if we don't want to close the info window yet, update infowindow data and return early
            if(!closeWindow){

                const updatedCurrentLine = {
                    ...currentLine,
                    start: {
                        ...currentLine.start,
                        POIID: updatedPoiID
                    }
                }

                setCurrentLine(updatedCurrentLine)
                setCurrentClickedPoint(prevPoint => {
                    return {
                        ...prevPoint,
                        POIID: updatedPoiID,
                        CreatedBy: createdBy,
                        Description: iwData.Description, 
                        poiGroups: iwPoiGroups,
                        linkedPOIs: updatedLinkedPOIs
                    }
                })
                setIwData(prevData => {
                    return {
                        ...prevData,
                        POIID: updatedPoiID,
                        CreatedBy: createdBy,
                        Description: iwData.Description, 
                        poiGroups: iwPoiGroups,
                        linkedPOIs: updatedLinkedPOIs
                    }
                })

                //since we saved, reset line before drag, since we don't need this anymore
                //only needed when we close window without saving, so we can put line back to how it was
                markerManager.setLineBeforeDrag(null)

                hideLoading()

                return {
                    success: true,
                    data: {
                        POIID: updatedPoiID
                    }
                }
                
            }

            //close window and reset lines
            poiService.resetLines(polylineManager, currentLine, updatedPoiID);
                
            infoWindow.closeInfoWindow();
            resetCurrentLineState();    

            hideLoading();
            timeoutSuccess("Saved");

            return {
                success: true,
                data: {
                    POIID: updatedPoiID
                }
            }

        } else {
            hideLoading();

            showError(response.message);

            return {
                success: false,
                message: response.message
            }
        }
    };


    const handlePoiDelete = async(poi) => {
        //confirm that user wants to delete poi
        const isConfirmed = await requestConfirm("Are you sure you want to delete this POI?")
        if(!isConfirmed) return

        //if confirmed, call api
        const response = await poiService.deletePOI(poi);

        //close window, reset lines and remove deleted point from state
        if (response.success) {
            closeWindowAndRemoveMarker(poi.POIID)
        } else {
            showError(response.message);
        }
    }


    const handleDialogCancel = async() => {
        //if there is unsaved work, ask user to confirm if they want to continue
        if(hasUnsavedChanges){
            const isConfirmed = await requestConfirm("This POI has unsaved work - are you sure you want to cancel?")

            if(!isConfirmed){
                return {
                    isClosed: false
                }
            }
        }

        //check if new poi or existing poi to decide how to reset the map
        if (currentLine?.start?.POIID) {
            const addLineToState = markerManager.lineBeforeDrag ?? currentLine;
            poiService.resetLines(polylineManager, addLineToState);
        } else if (currentLine) {
            poiService.removeUnsavedClickedPoint(currentLine.start)
            poiService.resetLines(polylineManager);

        }

        //close the window and reset state
        infoWindow.closeInfoWindow();
        resetCurrentLineState();
        setActiveComponent("PoiDialog")

        return {
            isClosed: true
        }

    };

    // Handle POI name change
    const handlePoiNameChange = (value) => {
        setIwData((prevData) => ({
            ...prevData,
            POI_Name: value,
        }));

        setHasUnsavedChanges(true)
    };

    // Handle POI description change
    const handlePoiDescriptionChange = (value) => {
        setIwData((prevData) => ({
            ...prevData,
            Description: value,
        }));

        setHasUnsavedChanges(true)
    };

    // Handle snapping to different points (US/DS Site/Subchain)
    const snapTo = (snapToSelection) => {
        if (snapToSelection === 'Select...' || snapToSelection === '') return;

        //save original line in state in case user cancels
        if(!markerManager.lineBeforeDrag){
            markerManager.setLineBeforeDrag(currentLine)
            setHasUnsavedChanges(true)
        }

        let lineEnd = {};
        switch (snapToSelection) {
            case 'US Site':
                lineEnd.lat = iwData.upstreamAndDownstreamAgms.upstreamAgm.Real_Lat || iwData.upstreamAndDownstreamAgms.upstreamAgm.Est_Lat;
                lineEnd.lng = iwData.upstreamAndDownstreamAgms.upstreamAgm.Real_Lng || iwData.upstreamAndDownstreamAgms.upstreamAgm.Est_Lng;
                break;
            case 'DS Site':
                lineEnd.lat = iwData.upstreamAndDownstreamAgms.downstreamAgm.Real_Lat || iwData.upstreamAndDownstreamAgms.downstreamAgm.Est_Lat;
                lineEnd.lng = iwData.upstreamAndDownstreamAgms.downstreamAgm.Real_Lng || iwData.upstreamAndDownstreamAgms.downstreamAgm.Est_Lng;
                break;
            case 'US Subchain':
                lineEnd.lat = iwData.upstreamAndDownstreamAgms.upstreamSc.lat;
                lineEnd.lng = iwData.upstreamAndDownstreamAgms.upstreamSc.lng;
                break;
            case 'DS Subchain':
                lineEnd.lat = iwData.upstreamAndDownstreamAgms.downstreamSc.lat;
                lineEnd.lng = iwData.upstreamAndDownstreamAgms.downstreamSc.lng;
                break;
            default:
                return;
        }

        polylineManager.clearCurrentPolyline();

        setCurrentLine({ start: currentClickedPoint, end: lineEnd })

        polylineManager.addCurrentPolyline({ start: currentClickedPoint, end: lineEnd });

        markerManager.setDraggableMarkerPosition(lineEnd);

        const poiMetersToPipeline = haversineDistanceMeters(currentClickedPoint.lat, currentClickedPoint.lng, lineEnd.lat, lineEnd.lng);
        let usDsDistances = getUSandDSDists(iwData.upstreamAndDownstreamAgms.upstreamAgm, iwData.upstreamAndDownstreamAgms.downstreamAgm, lineEnd);

        setIwData((prevData) => ({
            ...prevData,
            poiMetersToPipeline,
            usDsDistances,
            lineIntersect: lineEnd,
        }));
    };

    // Handle custom intersection point input with validation
    const handleCustomIntersect = (customLat, customLng) => {
        const closestSegment = findClosestSegment({ lat:customLat, lng:customLng }, polyLineArr);
        
        if (!closestSegment || !closestSegment.closestSegment) {
            return { success: false, errorMessage: 'No nearby pipeline segment found' };
        }
        
        // Validate custom point is within 10 meters of pipeline
        const validation = validateCustomIntersectPoint(
            { lat: customLat, lng: customLng },
            closestSegment.closestSegment.upstream,
            closestSegment.closestSegment.downstream,
            10
        );

        if (!validation.isValid) {
            // Return validation error so modal can display it
            return {
                success: false,
                errorMessage: validation.errorMessage
            };
        }

        // If valid, save original line in state in case user cancels later
        if (!markerManager.lineBeforeDrag) {
            markerManager.setLineBeforeDrag(currentLine);
            setHasUnsavedChanges(true);
        }

        const customIntersect = { lat: customLat, lng: customLng };

        // Update polyline visualization
        polylineManager.clearCurrentPolyline();
        setCurrentLine({ start: currentClickedPoint, end: customIntersect });
        polylineManager.addCurrentPolyline({ start: currentClickedPoint, end: customIntersect });

        // Update draggable marker position
        markerManager.setDraggableMarkerPosition(customIntersect);

        // Recalculate dependent values using same logic as snapTo
        const poiMetersToPipeline = haversineDistanceMeters(
            currentClickedPoint.lat,
            currentClickedPoint.lng,
            customIntersect.lat,
            customIntersect.lng
        );
        const usDsDistances = getUSandDSDists(
            iwData.upstreamAndDownstreamAgms.upstreamAgm,
            iwData.upstreamAndDownstreamAgms.downstreamAgm,
            customIntersect
        );

        // Update state, preserving all existing fields (poiGroups, linkedPOIs, CreatedBy, etc.)
        setIwData((prevData) => ({
            ...prevData,
            poiMetersToPipeline,
            usDsDistances,
            lineIntersect: customIntersect,
        }));

        setHasUnsavedChanges(true);

        return {
            success: true
        };
    };

    
    /*********************************************************************** */
    /* POI GROUP HANDLERS
    /*********************************************************************** */

    const handleUpdateLinkedPOI = async(poiGroupId, linkedPOI, poiId, closeWindow = true) => {
        showLoading("Saving POI details...")

        const response = await poiGroupService.updateLinkedPoi(poiGroupId, linkedPOI)
        
        const allLinkedPOIsResponse = await getLinkedPOIsAPI(poiId)

        //update state if successful - clickedPoint and info window must have new groups, linkedPOIs, and altName
        if(response.success){
            setHasUnsavedChanges(false)

            //only want to update the POI name/description on dialog box IF the linked poi we just edited is the same as the POI group selected in map component
            const lpoiMatchesSelectedGroup = poiGroupId === selectedPoiGroup.value

            //if message from group service, show dialog box
            if(response.msg){
                hideLoading()

                timeoutSuccess(response.msg)
                return response
            }


            const updatedLinkedPOIs = allLinkedPOIsResponse?.success ? allLinkedPOIsResponse.data : []

            //update info window data
            setIwData((prevData) => ({
                ...prevData,
                linkedPOIs: updatedLinkedPOIs,

                ...(lpoiMatchesSelectedGroup && linkedPOI.POI_Name && { AltPOI_Name: linkedPOI.POI_Name }),
                ...(lpoiMatchesSelectedGroup && linkedPOI.Description && { AltDescription: linkedPOI.Description })
            }));

            //update clicked points data separately
            poiService.setClickedPoints((prevPoints) =>{
                const newpoints = prevPoints.map((point) => {
                    
                    if(point.lineID === currentLine.start.lineID){
                        return {
                            ...point,
                            linkedPOIs: updatedLinkedPOIs,
                            ...(lpoiMatchesSelectedGroup && linkedPOI.POI_Name && { AltPOI_Name: linkedPOI.POI_Name }), //return new AltPOI_Name if it exist in original point and in the linkedpoi above
                            ...(lpoiMatchesSelectedGroup && linkedPOI.Description && { AltDescription: linkedPOI.Description })
                        }
                    } else {
                        return point
                    }

                }
                    
                )
                return newpoints
                }
            );

            setCurrentClickedPoint(prevPoint => {
                return {
                    ...prevPoint,
                    linkedPOIs: updatedLinkedPOIs,
                    ...(lpoiMatchesSelectedGroup && linkedPOI.POI_Name && { AltPOI_Name: linkedPOI.POI_Name }),
                    ...(lpoiMatchesSelectedGroup&& linkedPOI.Description && { AltDescription: linkedPOI.Description })
                }
            })


            if(closeWindow){
                closeDialogBox(poiId)
            }

            timeoutSuccess("POI details updated successfully")

        } else{
            showError(response.message);
        }
        hideLoading()
        return response
    }

    const handleAddPoiToGroup = async (selectedGroupDialog, poi, JobID) => {
        if(!selectedGroupDialog){
            showError("POI group must be selected")
            return {
                success: false,
                msg: "POI group must be selected"
            }
        }

        showLoading("Saving POI to group...")

        const POIID = poi.POIID
        
        //call poi group service, and get linkedPOIs from api once complete
        const response = await poiGroupService.addPoiToGroup(selectedGroupDialog.value, poi, JobID)
        
        //update state if successful - clickedPoint and info window must have new groups, linkedPOIs, and altName
        if(response.success){
            //if message from group service, show dialog box
            if(response.msg){
                hideLoading()
                timeoutSuccess(response.msg)

                return response
            }

            await updateStateAfterGroupChange(response, POIID)

            closeDialogBox(POIID)

            timeoutSuccess("Saved")


        } else{
            showError(response.message);
        }

        hideLoading()

        return response
    }

    const handleRemovePoiFromGroup = async (selectedGroupDialog, poi, JobID) => {
        if(!selectedGroupDialog){
            showError("POI group must be selected")
            return {
                success: false,
                msg: "POI group must be selected"
            }
        }

        showLoading("Removing POI from group...")

        const POIID = poi.POIID
        
        //call poi group service, and get linkedPOIs from api once complete
        const response = await poiGroupService.removePoiFromGroup(selectedGroupDialog.value, poi, JobID)
        
        //update state if successful - clickedPoint and info window must have new groups, linkedPOIs, and altName
        if(response.success){
            //if message from group service, show dialog box
            if(response.msg){
                hideLoading()

                timeoutSuccess(response.msg)

                return response
            }

            //if group selected from map dropdown is the same as the group the POI was just removed from, remove from map and close dialog box
            if(selectedPoiGroup.value === selectedGroupDialog.value){

                closeWindowAndRemoveMarker(poi.POIID)

                hideLoading()

                return response
            }

            
            await updateStateAfterGroupChange(response, POIID)

            timeoutSuccess("Saved")

        } else{
            showError(response.message);
        }



        hideLoading()

        return response
    }

    //used to update groups in iwData and clickedPoints when a POI is added to or removed from a group
    const updateStateAfterGroupChange = async(response, POIID) => {
        //get updated group data from api, make sure info window and clicked point have this
        const updatedGroups = response.data.updatedGroups

        const allLinkedPOIsResponse = await getLinkedPOIsAPI(POIID)
        const updatedLinkedPOIs = allLinkedPOIsResponse.success ? allLinkedPOIsResponse.data : []

        //get the corresponding linkedPOI, if it exists
        //if there is an AltPOI_Name in this point, we fetched it as part of a group, which means POI_Name is displaying the alt name
        //there may have been a change in the alt name during the update, so update name here too to poi dialog has it
        const linkedPOI = updatedLinkedPOIs.find(lpoi => lpoi.POIID === POIID)
        const AltPOI_Name = linkedPOI?.POI_Name
        const AltDescription = linkedPOI?.Description

        //update info window data
        setIwData((prevData) => ({
            ...prevData,
            poiGroups: updatedGroups,
            linkedPOIs: updatedLinkedPOIs,

            ...(AltPOI_Name && { AltPOI_Name }),
            ...(AltDescription && { AltDescription })
        }));

        //update clicked points data separately
        poiService.setClickedPoints((prevPoints) =>{
            const newpoints = prevPoints.map((point) => {
                
                if(point.lineID === currentLine.start.lineID){

                    return {
                        ...point,
                        poiGroups: updatedGroups,
                        linkedPOIs: updatedLinkedPOIs,
                        ...(AltPOI_Name && { AltPOI_Name }), //return new AltPOI_Name if it exists in the linkedpoi above
                        ...(AltDescription && { AltDescription })

                    }
                } else {
                    return point
                }

            }
                
            )
            return newpoints
            }
        );

        setCurrentClickedPoint(prevPoint => {
            return {
                ...prevPoint,
                poiGroups: updatedGroups,
                linkedPOIs: updatedLinkedPOIs,
                ...(AltPOI_Name && { AltPOI_Name }),
                ...(AltDescription && { AltDescription })
            }
        })

    }

    /**
     * @typedef addNewPoiGroupData
     * @property {string} POIGroup_Name
     * @property {string} Description
     * @property {number} JobID
     * @property {Object[]} pois  {POIID: int, POI_Name: string}
     */

    /**
     * handleAddNewPoiGroup - calls api to create a new group. The api also places the indicated poi into the new group, and adds the logged-in user to the group they created. If successful, the new group is returned so the dropdown can fill in the new group data, and state is updated for clickedPoints and info window data.
     * 
     * @param {addNewPoiGroupData} groupData
     * 
     * @returns {Promise} - if successful, returns the new group, otherwise returns error message
     * 
     */
    const handleAddNewPoiGroup = async(groupData) => {
        showLoading("Adding POI group...")

        const POIID = groupData.pois[0].POIID

        const response = await poiGroupService.addNewPoiGroup(groupData)
        const allLinkedPOIsResponse = await getLinkedPOIsAPI(POIID)

        if(response.success){
            const newGroup = response.data

            //add new group to poiGroups in iwData and clickedpoint
            setIwData((prevData) => {
                const updatedGroups = [...prevData.poiGroups, newGroup]
                const updatedLinkedPOIs = allLinkedPOIsResponse.success ? allLinkedPOIsResponse.data : prevData.linkedPOIs

                return {
                    ...prevData,
                    poiGroups: updatedGroups,
                    linkedPOIs: updatedLinkedPOIs
                }
            });

            poiService.setClickedPoints((prevPoints) =>{

                const newpoints = prevPoints.map((point) =>
                    point.lineID === currentLine.start.lineID
                        ? { 
                            ...point, 
                            poiGroups: [...point.poiGroups, newGroup],
                            linkedPOIs: allLinkedPOIsResponse.success ? [...allLinkedPOIsResponse.data] : [...point.linkedPOIs]

                        }
                        : point
                )

                return newpoints
                }
            );

            setCurrentClickedPoint(prevPoint => {
                const updatedGroups = [...prevPoint.poiGroups, newGroup]
                const updatedLinkedPOIs = allLinkedPOIsResponse.success ? allLinkedPOIsResponse.data : prevPoint.linkedPOIs

                return {
                    ...prevPoint,
                    poiGroups: updatedGroups,
                    linkedPOIs: updatedLinkedPOIs
                }
            })

            setSelectedPoiGroup({
                "label": newGroup.POIGroup_Name,
                "value": newGroup.POIGroupID,
                "IsEditor": true,
                "IsUserInGroup": true
            })

            closeDialogBox(POIID)

            timeoutSuccess("POI group added successfully")

        } else {
            showError(response.message);
        }

        hideLoading()

        return response
    }

    const getSinglePOIEstimatedPassTime = async(poiId) => {
        if(!selectedPoiRun) {
            return {
                success: false
            }
        }

        const estimatedPassTime = await getPOIEstimatedPassTimeAPI(poiId, selectedPoiRun)

        return estimatedPassTime
    }

    
    /*********************************************************************** */
    /* OTHER HELPERS
    /*********************************************************************** */
    
    const resetCurrentLineState = () => {
        setCurrentLine({})
        setCurrentClickedPoint(null);
        markerManager.resetMarkerState()
    };

    //handle changing the active component of the poi dialog box here
    //we need to keep the activeComponent state within the poi hooks, because we need to be able to re-render (in the usePoiMap useEffect) the info window with fresh data when the component changes
    //sometimes when the infowindow re-renders, it will reset the component back to the default PoiDialog box, even if the Add To My Groups button was clicked and the PoiGroupDialog should be shown. The activeComponent state was lifted out of the dialog components and kept here so it could be saved across renders
    const handleChangeActiveComponent = (component) => {
        setActiveComponent(component)
    }

    const closeWindowAndRemoveMarker = (POIID) => {
        infoWindow.closeInfoWindow();
            
        resetCurrentLineState();

        poiService.resetLines(polylineManager);

        poiService.setClickedPoints((prevPoints) => {
            const updatedArr = prevPoints.filter(point => point.POIID !== POIID);
            return updatedArr;
        });
    }

    const closeDialogBox = (poiId) => {
        poiService.resetLines(polylineManager, currentLine, poiId);
                
        infoWindow.closeInfoWindow();
        resetCurrentLineState();
    }

    return {
        handlePoiSave,
        handlePoiDelete,
        handleDialogCancel,
        handlePoiNameChange,
        handlePoiDescriptionChange,
        snapTo,
        handleCustomIntersect,
        handleAddNewPoiGroup,
        handleChangeActiveComponent,
        handleAddPoiToGroup,
        handleRemovePoiFromGroup,
        handleUpdateLinkedPOI,
        getSinglePOIEstimatedPassTime,
    };
};

export default usePoiDialogHandlers;
